import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import type { ApiAccessKey } from '../../@types/ApiAccessKey';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import { postJSON, type RequestError } from '../../fetch';
import type { ApiResponse } from '../../Http';
import { useAuth } from '../../State/Auth';
import useSnackbar from '../../State/Snackbar/useSnackbar';
import { getMarketCodeFromToken } from '../../utils/token';
import { createRequestState } from '../utils';
import { VEHICLE_VIEW_QUERY } from '../vehicle-view/useGetVehicle';

interface UseRemoveAssessRiskFromVehicleVariables {
  vin: string;
}

const useRemoveAssessRiskFromVehicle = (): [
  (variables: UseRemoveAssessRiskFromVehicleVariables) => void,
  RequestStateQuery,
] => {
  const { token, decodedToken } = useAuth();
  const { pushSnackbarError, pushSnackbarSuccess } = useSnackbar();
  const queryClient = useQueryClient();

  const mutation = useMutation<void, RequestError, UseRemoveAssessRiskFromVehicleVariables>({
    mutationFn: async (payload: UseRemoveAssessRiskFromVehicleVariables) => {
      const marketCode = getMarketCodeFromToken(decodedToken);
      await postJSON<ApiResponse<ApiAccessKey>>(
        `/api/vehicle/${marketCode.toLowerCase()}/assess-risk/vin/${payload.vin}`,
        token!,
      );
    },
    onSuccess: async (_, payload) => {
      await queryClient.refetchQueries({
        queryKey: [VEHICLE_VIEW_QUERY, payload.vin],
      });
      pushSnackbarSuccess(
        <>
          Successfully removed risk assessment from vehicle: <b>{payload.vin}</b>
        </>,
      );
    },
    onError: (_, payload) => {
      pushSnackbarError(
        <>
          Error occured while trying to remove risk assessment for vehicle: <b>{payload.vin}</b>,
          please try again.
        </>,
      );
    },
  });

  return [
    (variables: UseRemoveAssessRiskFromVehicleVariables) => mutation.mutate(variables),
    createRequestState(mutation),
  ];
};

export default useRemoveAssessRiskFromVehicle;
